import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      return;
    }
    setStatus("loading");
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch("https://readdy.ai/api/form/d86276a1csuacpqtd99g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setEmail("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <footer className="bg-foreground-900 text-background-50 pt-20 md:pt-28 pb-10">
      <div className="px-6 md:px-12">
        {/* Top: huge mark + newsletter */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-20 md:mb-28">
          <div>
            <h3 className="font-display font-black text-background-50 text-5xl md:text-7xl leading-[0.9] tracking-[-0.03em] mb-8">
              BEGIN YOUR <br />
              <em className="font-serif italic font-light">legacy</em> TODAY.
            </h3>
            <p className="text-background-50/70 text-base md:text-lg font-serif leading-relaxed max-w-md">
              Join our community for studio notes, artist features, guest residencies, and early access to booking windows. No noise, only reverence.
            </p>
          </div>

          <div className="lg:pt-4">
            <p className="font-display text-xs uppercase tracking-[0.25em] text-background-50/60 mb-4">
              The Archive
            </p>
            <p className="text-background-50/80 text-sm mb-6 font-serif">
              Studio notes, new collections, and seasonal booking windows.
            </p>
            <form
              id="newsletter-form-tattoo-collective"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (status !== "idle") setStatus("idle");
                }}
                placeholder="your@email.com"
                required
                className="flex-1 bg-transparent border-b border-background-50/40 focus:border-accent-500 outline-none text-background-50 placeholder:text-background-50/40 px-1 py-3 text-sm transition-colors"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="px-6 py-3 bg-accent-500 text-foreground-950 font-display text-xs uppercase tracking-[0.2em] rounded-full hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {status === "loading" ? "Sending..." : "Subscribe"}
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </form>
            {status === "success" && (
              <p className="mt-4 text-xs text-accent-400">Welcome to the Archive.</p>
            )}
            {status === "error" && (
              <p className="mt-4 text-xs text-red-300">Please enter a valid email.</p>
            )}
          </div>
        </div>

        {/* Middle: columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-16 md:mb-20 pb-10 border-b border-background-50/15">
          <div>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-background-50/50 mb-4">
              Studio
            </p>
            <p className="text-sm text-background-50 font-serif leading-relaxed">
              14 South Union Avenue<br />Cranford, NJ 07016
            </p>
          </div>
          <div>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-background-50/50 mb-4">
              Contact
            </p>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="mailto:hello@fineartstattoo.com" className="link-underline text-background-50 cursor-pointer">
                  hello@fineartstattoo.com
                </a>
              </li>
              <li>
                <a href="tel:+19085550182" className="link-underline text-background-50 cursor-pointer">
                  +1 (908) 555-0182
                </a>
              </li>
            </ul>
          </div>
          <div>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-background-50/50 mb-4">
              Navigate
            </p>
            <ul className="space-y-2 text-sm">
              {[
                { l: "Marcus Aurelius", to: "/artist/marcus-aurelius" },
                { l: "Isabella Rossi", to: "/artist/isabella-rossi" },
                { l: "Cassius Vel", to: "/artist/cassius-vel" },
                { l: "Seraphina Dor", to: "/artist/seraphina-dor" },
              ].map((item) => (
                <li key={item.l}>
                  <Link
                    to={item.to}
                    className="link-underline text-background-50 cursor-pointer"
                  >
                    {item.l}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-background-50/50 mb-4">
              Social
            </p>
            <ul className="space-y-2 text-sm">
              {[
                { l: "Instagram", i: "ri-instagram-line" },
                { l: "Pinterest", i: "ri-pinterest-line" },
                { l: "YouTube", i: "ri-youtube-line" },
                { l: "TikTok", i: "ri-tiktok-line" },
              ].map((s) => (
                <li key={s.l}>
                  <a
                    href="#"
                    className="inline-flex items-center gap-2 link-underline text-background-50 cursor-pointer"
                  >
                    <i className={`${s.i} text-base`}></i>
                    {s.l}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Giant signature */}
        <div className="overflow-hidden mb-10">
          <div className="font-display font-black text-background-50/95 leading-none tracking-[-0.05em] text-[18vw] md:text-[14vw] whitespace-nowrap">
            FINE&nbsp; &nbsp; &nbsp; &nbsp;ARTS&nbsp;
          </div>
        </div>

        {/* Bottom */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-background-50/55 font-display uppercase tracking-[0.18em]">
          <p>&copy; 2035 Fine Arts Tattoo Collective. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-6">
            <a href="#" className="link-underline cursor-pointer">Privacy</a>
            <a href="#" className="link-underline cursor-pointer">Accessibility</a>
            <a href="#" className="link-underline cursor-pointer">Imprint</a>
          </div>
        </div>
      </div>
    </footer>
  );
}